import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore, hasActiveContract, hasActiveContractRole, hasAnyActiveContract } from '@stores/authStore'
import { isForceV1 } from '@/config/designV2'
import {
  LayoutDashboard,
  Building2,
  FileText,
  Search,
  Settings,
  LogOut,
  X,
  ChevronDown,
  ChevronRight,
  User,
  ArrowLeft,
  Database,
  RefreshCw,
  Mail,
  Bot,
  Layers,
  Monitor,
  UsersRound,
  Percent,
  Bookmark,
  ShieldCheck,
  ScanFace,
  Home,
  ScrollText,
  Sparkles,
  Workflow,
  CheckSquare,
  Brain,
  Users,
  Activity,
  Terminal,
  Landmark,
} from 'lucide-react'
import { useState, useCallback } from 'react'
import DisplayModeSwitcher from './ModeSwitcher'
import { getPersistedMode, persistMode } from '@/config/designV2'
import type { V2Mode } from '@/config/designV2'

// Brand logo mark with gradient
function BrandLogo({ brand }: { brand: string }) {
  const letter = brand === 'uniplacement' ? 'U' : brand === 'up' ? 'UP' : 'I'
  return (
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 rounded-xl bg-[var(--v2-brand)] flex items-center justify-center shadow-sm">
        <span className="text-[var(--v2-brand-text)] font-bold text-lg leading-none">{letter}</span>
      </div>
      <span className="text-base font-semibold text-[var(--v2-text)]">
        {brand === 'uniplacement' ? 'Uniplacement' : brand === 'up' ? 'UP' : 'Immotron'}
      </span>
    </div>
  )
}

// Brand switcher dropdown (Immotron / Uniplacement / UP)
function BrandSwitcher({ brand, onBrandChange }: { brand: string; onBrandChange: (b: string) => void }) {
  const [open, setOpen] = useState(false)
  const brands = [
    { key: 'immotron', label: 'Immotron', letter: 'I' },
    { key: 'uniplacement', label: 'Uniplacement', letter: 'U' },
    { key: 'up', label: 'UP', letter: 'UP' },
  ]
  const current = brands.find((b) => b.key === brand) || brands[0]

  return (
    <div className="relative">
      <button
        data-testid="sidebar-v2-open-btn"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm text-[var(--v2-text-secondary)] hover:bg-[var(--v2-hover-overlay)] transition-colors"
      >
        <div className="w-6 h-6 rounded-md bg-[var(--v2-brand)] flex items-center justify-center">
          <span className="text-[var(--v2-brand-text)] font-bold text-xs leading-none">{current.letter}</span>
        </div>
        <span className="flex-1 text-left text-[var(--v2-text)] font-medium">{current.label}</span>
        <ChevronDown size={14} className={`text-[var(--v2-text-tertiary)] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-[55]" onClick={() => setOpen(false)} />
          <div className="absolute bottom-full left-0 right-0 mb-1 bg-[var(--v2-surface-raised)] rounded-lg shadow-lg border border-[var(--v2-border)] py-1 z-[60]">
            {brands.map((b) => (
              <button
                data-testid="sidebar-v2-brand-change-btn"
                key={b.key}
                onClick={() => { onBrandChange(b.key); setOpen(false) }}
                className={`flex items-center gap-2 w-full px-3 py-2 text-sm transition-colors ${
                  b.key === brand ? 'bg-[var(--v2-brand-subtle)] text-[var(--v2-brand)] font-medium' : 'text-[var(--v2-text-secondary)] hover:bg-[var(--v2-hover-overlay)]'
                }`}
              >
                <div className={`w-6 h-6 rounded-md flex items-center justify-center ${
                  b.key === brand
                    ? 'bg-[var(--v2-brand)]'
                    : 'bg-[var(--v2-border)]'
                }`}>
                  <span className={`font-bold text-xs leading-none ${b.key === brand ? 'text-[var(--v2-brand-text)]' : 'text-[var(--v2-text-tertiary)]'}`}>
                    {b.letter}
                  </span>
                </div>
                <span>{b.label}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

type NavItem = {
  key: string
  label: string
  icon: React.ReactNode
  path: string
  disabled?: boolean
  tooltip?: string
}
type NavSection = { key: string; label: string | null; items: NavItem[] }

interface SidebarV2Props {
  isOpen: boolean
  onClose: () => void
  /** Brand (immotron/uniplacement/up) — optional, falls back to local state */
  brand?: string
  onBrandChange?: (brand: string) => void
}

export default function SidebarV2({ isOpen, onClose, brand: controlledBrand, onBrandChange }: SidebarV2Props) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAuthStore()
  // Detect v1 vs v2 at component level — controls styling + footer visibility
  const isV2 = user?.design_version === 'v2' && !isForceV1()
  const [localBrand, setLocalBrand] = useState('immotron')
  const brand = controlledBrand ?? localBrand
  const handleBrandChange = onBrandChange ?? setLocalBrand
  // Display mode (light/dim/dark) — persisted via designV2
  const [displayMode, setDisplayMode] = useState<V2Mode>(getPersistedMode)
  const handleDisplayModeChange = useCallback((newMode: V2Mode) => {
    setDisplayMode(newMode)
    persistMode(newMode)
  }, [])
  const [settingsExpanded, setSettingsExpanded] = useState(
    location.pathname.startsWith('/admin/settings')
  )

  const isSettingsRoute = location.pathname.startsWith('/admin/settings')

  // Build navigation sections based on role
  const getNavSections = (): NavSection[] => {
    const sections: NavSection[] = []

    // Übersicht — for investor: only show dashboard if any active contract
    const isInvestorOnly = user?.role === 'investor'
    const showDashboard = !isInvestorOnly || hasAnyActiveContract()
    if (showDashboard) {
      sections.push({
        key: 'overview',
        label: null,
        items: [
          {
            key: 'dashboard',
            label: t('navigation.dashboard'),
            icon: <LayoutDashboard size={20} />,
            path: '/dashboard',
          },
          ...((user?.role === 'admin' || user?.role === 'sales') ? [{
            key: 'tasks',
            label: t('tasks.title'),
            icon: <CheckSquare size={20} />,
            path: '/tasks',
          }, {
            key: 'emails',
            label: t('navigation.emails', 'Emails'),
            icon: <Mail size={20} />,
            path: '/emails',
          }] : []),
        ],
      })
    }

    // CRM — /crm/* routes for all admin/sales users
    if (user?.role === 'admin' || user?.role === 'sales') {
      sections.push({
        key: 'crm',
        label: t('navigation.sectionCRM'),
        items: [
          {
            key: 'organizations',
            label: t('navigation.companies'),
            icon: <Building2 size={20} />,
            path: '/crm/organizations',
          },
          {
            key: 'fundRadar',
            label: t('navigation.fundRadar', 'Fondsradar'),
            icon: <Landmark size={20} />,
            path: '/crm/funds',
          },
          {
            key: 'contacts',
            label: t('navigation.contacts'),
            icon: <Users size={20} />,
            path: '/crm/contacts',
          },
          {
            key: 'activities',
            label: t('navigation.activities', 'Aktivitäten'),
            icon: <Activity size={20} />,
            path: '/crm/activities',
          },
          {
            key: 'campaigns',
            label: t('navigation.campaigns'),
            icon: <Mail size={20} />,
            path: '/crm/campaigns',
          },
        ],
      })
    }

    // Immobilien (admin + sales)
    if (user?.role === 'admin' || user?.role === 'sales') {
      sections.push({
        key: 'immobilien',
        label: t('navigation.sectionImmobilien', 'Immobilien'),
        items: [
          {
            key: 'properties',
            label: t('navigation.properties'),
            icon: <Building2 size={20} />,
            path: '/sales/properties',
          },
          {
            key: 'listings',
            label: t('navigation.listings'),
            icon: <Layers size={20} />,
            path: '/sales/listings',
          },
          {
            key: 'qualityGate',
            label: t('navigation.qualityGate'),
            icon: <ShieldCheck size={20} />,
            path: '/sales/quality-gate',
          },
          {
            key: 'contracts',
            label: t('navigation.contracts'),
            icon: <FileText size={20} />,
            path: '/sales/contracts',
          },
        ],
      })
    }

    // KI-Tools (admin + sales)
    if (user?.role === 'admin' || user?.role === 'sales') {
      sections.push({
        key: 'aiTools',
        label: t('navigation.sectionAI', 'KI-Tools'),
        items: [
          {
            key: 'aiUpload',
            label: t('navigation.aiUpload', 'Objekterfassung'),
            icon: <Sparkles size={20} />,
            path: '/sales/properties/new',
          },
          {
            key: 'aiJobs',
            label: t('navigation.aiJobs', 'KI-Jobs'),
            icon: <Bot size={20} />,
            path: '/sales/ai-jobs',
          },
          {
            key: 'aiLearning',
            label: t('navigation.aiLearning', 'Lern-Modus'),
            icon: <Brain size={20} />,
            path: '/sales/ai-learning',
          },
          {
            key: 'ceoAgent',
            label: t('navigation.ceoAgent', 'Pendenzen-Assistent'),
            icon: <Bot size={20} />,
            path: '/admin/ceo-agent',
          },
        ],
      })
    }

    // Investor Kaufen-Sektion
    if (user?.role === 'investor') {
      const buyerActive = hasActiveContract('PLATFORM_AGREEMENT')
      sections.push({
        key: 'investor',
        label: t('navigation.sectionInvestor'),
        items: [
          {
            key: 'search',
            label: t('navigation.search'),
            icon: <Search size={20} />,
            path: '/investor/search',
            disabled: !buyerActive,
            tooltip: !buyerActive ? t('navigation.contractExpiredTooltip') : undefined,
          },
          {
            key: 'favorites',
            label: t('navigation.favorites'),
            icon: <Bookmark size={20} />,
            path: '/investor/favorites',
            disabled: !buyerActive,
            tooltip: !buyerActive ? t('navigation.contractExpiredTooltip') : undefined,
          },
          {
            key: 'myOffers',
            label: t('navigation.myOffers'),
            icon: <FileText size={20} />,
            path: '/investor/offers',
            disabled: !buyerActive,
            tooltip: !buyerActive ? t('navigation.contractExpiredTooltip') : undefined,
          },
          {
            key: 'myContracts',
            label: t('navigation.myContracts'),
            icon: <ScrollText size={20} />,
            path: '/investor/contracts',
          },
        ],
      })
    }

    // Seller section — only for investors who actually signed as SELLER
    if (user?.role === 'investor') {
      const sellerActive = hasActiveContractRole('PLATFORM_AGREEMENT', 'SELLER')
      if (sellerActive) {
        sections.push({
          key: 'seller',
          label: t('navigation.sectionSeller'),
          items: [
            {
              key: 'myProperties',
              label: t('navigation.myProperties'),
              icon: <Home size={20} />,
              path: '/seller/properties',
            },
          ],
        })
      }
    }

    return sections
  }

  const navSections = getNavSections()

  // Settings sub-navigation items
  const settingsNavItems = [
    { key: 'mandanten', label: t('settings.mandanten'), icon: <Building2 size={20} />, path: '/admin/settings/mandanten' },
    { key: 'schema', label: t('settings.schema'), icon: <Database size={20} />, path: '/admin/settings/schema' },
    { key: 'zefixSync', label: t('settings.zefixSync'), icon: <RefreshCw size={20} />, path: '/admin/settings/zefix-sync' },
    { key: 'regulatedWhitelist', label: t('settings.regulatedWhitelist'), icon: <ShieldCheck size={20} />, path: '/admin/settings/regulated-whitelist' },
    { key: 'kycReview', label: t('settings.kycReview'), icon: <ScanFace size={20} />, path: '/admin/settings/kyc-review' },
    { key: 'emailTemplates', label: t('navigation.emailTemplates'), icon: <Mail size={20} />, path: '/admin/settings/email-templates' },
    { key: 'investorGroups', label: t('settings.investorGroups'), icon: <UsersRound size={20} />, path: '/admin/settings/investor-groups' },
    { key: 'ai', label: t('settings.ai'), icon: <Bot size={20} />, path: '/admin/settings/ai' },
    { key: 'commissions', label: t('settings.commissions'), icon: <Percent size={20} />, path: '/admin/settings/commissions' },
    { key: 'processes', label: t('settings.processes'), icon: <Workflow size={20} />, path: '/admin/settings/processes' },
    { key: 'system', label: t('settings.system'), icon: <Monitor size={20} />, path: '/admin/settings/system' },
  ]

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  // Check if a nav item path matches the current location (including query params)
  const isNavActive = (path: string): boolean => {
    if (path.includes('?')) {
      const [pathname, search] = path.split('?')
      return location.pathname === pathname && location.search.includes(search)
    }
    return location.pathname === path
  }

  const renderNavItem = (item: NavItem) => {
    if (item.disabled) {
      return (
        <div
          key={item.key}
          title={item.tooltip}
          className="flex items-center gap-3 px-3 py-2 rounded-lg opacity-50 cursor-not-allowed text-[var(--v2-text-tertiary)]"
          aria-disabled="true"
        >
          {item.icon}
          <span className="text-sm">{item.label}</span>
        </div>
      )
    }

    // For paths with query params, use onClick instead of NavLink
    if (item.path.includes('?')) {
      const active = isNavActive(item.path)
      return (
        <button
          data-testid="sidebar-v2-close-btn-1"
          key={item.key}
          onClick={() => { navigate(item.path); onClose() }}
          className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors w-full text-left ${
            active
              ? 'bg-[var(--v2-brand-subtle)] text-[var(--v2-brand)] font-medium'
              : 'text-[var(--v2-text-secondary)] hover:bg-[var(--v2-hover-overlay)]'
          }`}
        >
          {item.icon}
          <span className="text-sm">{item.label}</span>
        </button>
      )
    }

    return (
      <NavLink
        data-testid="sidebar-v2-close-link-1"
        key={item.key}
        to={item.path}
        end
        onClick={onClose}
        className={({ isActive }) =>
          `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
            isActive
              ? 'bg-[var(--v2-brand-subtle)] text-[var(--v2-brand)] font-medium'
              : 'text-[var(--v2-text-secondary)] hover:bg-[var(--v2-hover-overlay)]'
          }`
        }
      >
        {item.icon}
        <span className="text-sm">{item.label}</span>
      </NavLink>
    )
  }

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full w-64 bg-[var(--v2-surface)] border-r border-[var(--v2-border)]
          flex flex-col
          transform transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0`}
        {...(!isV2 ? { style: {
          '--v2-surface': '#ffffff',
          '--v2-surface-raised': '#f9fafb',
          '--v2-surface-overlay': '#ffffff',
          '--v2-surface-inset': '#f3f4f6',
          '--v2-text': '#111827',
          '--v2-text-secondary': '#4b5563',
          '--v2-text-tertiary': '#9ca3af',
          '--v2-text-inverse': '#ffffff',
          '--v2-border': '#e5e7eb',
          '--v2-border-strong': '#d1d5db',
          '--v2-hover-overlay': 'rgba(0, 0, 0, 0.04)',
          '--v2-brand-subtle': 'rgba(232, 54, 75, 0.08)',
          '--v2-shadow-sm': '0 1px 2px rgba(0, 0, 0, 0.06)',
          '--v2-shadow-md': '0 4px 12px rgba(0, 0, 0, 0.08)',
        } as React.CSSProperties } : {})}
      >
        {/* Logo Header */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-[var(--v2-border)] shrink-0">
          {isV2 ? <BrandLogo brand={brand} /> : (
            <span className="text-base font-semibold text-[var(--v2-text)]">Immotron</span>
          )}
          <button
            data-testid="sidebar-v2-close-btn-2"
            className="lg:hidden p-1 text-[var(--v2-text-tertiary)] hover:text-[var(--v2-text-secondary)] rounded-md"
            onClick={onClose}
          >
            <X size={20} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-1">
          {isSettingsRoute ? (
            <>
              {/* Back to main navigation */}
              <NavLink
                data-testid="sidebar-v2-close-link-2"
                to="/dashboard"
                onClick={onClose}
                className="flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-[var(--v2-text-secondary)] hover:bg-[var(--v2-hover-overlay)] mb-2"
              >
                <ArrowLeft size={20} />
                <span className="text-sm">{t('common.back')}</span>
              </NavLink>
              <div className="px-3 mb-1.5">
                <h3 className="text-[11px] font-semibold text-[var(--v2-text-tertiary)] uppercase tracking-wider">
                  {t('settings.title')}
                </h3>
              </div>
              {settingsNavItems.map((item) => (
                <NavLink
                  data-testid="sidebar-v2-close-link-3"
                  key={item.key}
                  to={item.path}
                  end
                  onClick={onClose}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                      isActive
                        ? 'bg-[var(--v2-brand-subtle)] text-[var(--v2-brand)] font-medium'
                        : 'text-[var(--v2-text-secondary)] hover:bg-[var(--v2-hover-overlay)]'
                    }`
                  }
                >
                  {item.icon}
                  <span className="text-sm">{item.label}</span>
                </NavLink>
              ))}
            </>
          ) : (
            <>
              {navSections.map((section) => (
                <div key={section.key} className={section.label ? 'mt-4' : ''}>
                  {section.label && (
                    <div className="px-3 mb-1.5">
                      <h3 className="text-[11px] font-semibold text-[var(--v2-text-tertiary)] uppercase tracking-wider">
                        {section.label}
                      </h3>
                    </div>
                  )}
                  {section.items.map(renderNavItem)}
                </div>
              ))}

              {/* Administration / Settings (admin + tenant_admin) — collapsible */}
              {/* Dev Command & Control (admin only) */}
              {user?.role === 'admin' && (
                <div className="mt-4">
                  <div className="px-3 mb-1.5">
                    <h3 className="text-[11px] font-semibold text-[var(--v2-text-tertiary)] uppercase tracking-wider">
                      Entwicklung
                    </h3>
                  </div>
                  <NavLink
                    to="/dev"
                    onClick={onClose}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                        isActive
                          ? 'bg-[var(--v2-brand-subtle)] text-[var(--v2-brand)] font-medium'
                          : 'text-[var(--v2-text-secondary)] hover:bg-[var(--v2-hover-overlay)]'
                      }`
                    }
                  >
                    <Terminal size={20} />
                    <span className="text-sm">Dev C&C</span>
                  </NavLink>
                </div>
              )}

              {(user?.role === 'admin' || user?.role === 'tenant_admin') && (
                <div className="mt-4">
                  <div className="px-3 mb-1.5">
                    <h3 className="text-[11px] font-semibold text-[var(--v2-text-tertiary)] uppercase tracking-wider">
                      {t('navigation.sectionAdmin', 'Administration')}
                    </h3>
                  </div>
                  <button
                    data-testid="sidebar-v2-settings-expanded-btn"
                    onClick={() => setSettingsExpanded(!settingsExpanded)}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-[var(--v2-text-secondary)] hover:bg-[var(--v2-hover-overlay)] w-full text-left"
                  >
                    <Settings size={20} />
                    <span className="text-sm flex-1">{t('navigation.settings')}</span>
                    <ChevronRight
                      size={16}
                      className={`text-[var(--v2-text-tertiary)] transition-transform ${settingsExpanded ? 'rotate-90' : ''}`}
                    />
                  </button>
                  {settingsExpanded && (
                    <div className="ml-4 mt-0.5 space-y-0.5">
                      {settingsNavItems.map((item) => (
                        <NavLink
                          data-testid="sidebar-v2-close-link-4"
                          key={item.key}
                          to={item.path}
                          end
                          onClick={onClose}
                          className={({ isActive }) =>
                            `flex items-center gap-3 px-3 py-1.5 rounded-lg transition-colors text-sm ${
                              isActive
                                ? 'bg-[var(--v2-brand-subtle)] text-[var(--v2-brand)] font-medium'
                                : 'text-[var(--v2-text-tertiary)] hover:bg-[var(--v2-hover-overlay)] hover:text-[var(--v2-text)]'
                            }`
                          }
                        >
                          {item.icon}
                          <span>{item.label}</span>
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </nav>

        {/* Footer */}
        <div className="shrink-0 border-t border-[var(--v2-border)]">
          {/* Display Mode Switcher (Light/Dim/Dark) — v2 only */}
          {isV2 && (
            <div className="px-3 pt-3 pb-1">
              <DisplayModeSwitcher currentMode={displayMode} onChange={handleDisplayModeChange} />
            </div>
          )}

          {/* Brand Switcher — v2 only (v1 has company switcher in header) */}
          {isV2 && (
            <div className="px-3 pb-1">
              <BrandSwitcher brand={brand} onBrandChange={handleBrandChange} />
            </div>
          )}

          {/* User Info + Settings */}
          <div className="px-3 pb-3 pt-1">
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[var(--v2-hover-overlay)] transition-colors">
              <div className="w-8 h-8 bg-[var(--v2-brand-subtle)] rounded-full flex items-center justify-center shrink-0">
                <User size={16} className="text-[var(--v2-brand)]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[var(--v2-text)] truncate">
                  {user?.first_name || user?.email}
                </div>
                <div className="text-xs text-[var(--v2-text-tertiary)] truncate">
                  {t(`roles.${user?.role}`)}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <NavLink
                  data-testid="sidebar-v2-close-link-5"
                  to="/settings"
                  onClick={onClose}
                  className="p-1.5 text-[var(--v2-text-tertiary)] hover:text-[var(--v2-text-secondary)] rounded-md hover:bg-[var(--v2-hover-overlay)] transition-colors"
                  title={t('navigation.settings')}
                >
                  <Settings size={16} />
                </NavLink>
                <button
                  data-testid="sidebar-v2-logout-btn"
                  onClick={handleLogout}
                  className="p-1.5 text-[var(--v2-text-tertiary)] hover:text-[var(--v2-error)] rounded-md hover:bg-[var(--v2-hover-overlay)] transition-colors"
                  title={t('auth.logout')}
                >
                  <LogOut size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </>
  )
}
